import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Package, Search, Pencil, Trash2, Plus, AlertCircle, GitMerge, Loader2, Archive, RotateCcw } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const ProductsPage = ({ onEditProduct }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [duplicates, setDuplicates] = useState([]);
  const [disp, setDisp] = useState(null);
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [mergeDialog, setMergeDialog] = useState(null); // { sourceId, targetId, sourceName, targetName }
  const [merging, setMerging] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Sheet width calc — must match Calculator formula. Used for the table column only.
  const calculateSheetWidth = (product) => {
    const { base, leftSide, rightSide, leftLip, rightLip, thickness } = product;
    const baseNum = parseFloat(base);
    const leftNum = parseFloat(leftSide);
    const rightNum = parseFloat(rightSide);
    const leftLipNum = parseFloat(leftLip) || 0;
    const rightLipNum = parseFloat(rightLip) || 0;
    const thicknessNum = parseFloat(thickness);
    const numAngles = 2 + (leftLipNum > 0 ? 1 : 0) + (rightLipNum > 0 ? 1 : 0);
    const expansion = numAngles * 2 * thicknessNum;
    return (baseNum + leftNum + rightNum + leftLipNum + rightLipNum - expansion).toFixed(1);
  };

  const refresh = useCallback(async () => {
    try {
      const [pRes, dRes, dispRes] = await Promise.all([
        fetch(`${API_URL}/api/presets?include_deprecated=true`),
        fetch(`${API_URL}/api/presets/duplicates`),
        fetch(`${API_URL}/api/settings/display`),
      ]);
      if (pRes.ok) setProducts(await pRes.json());
      if (dRes.ok) setDuplicates(await dRes.json());
      if (dispRes.ok) setDisp(await dispRes.json());
    } catch (e) {
      console.error('Error fetching products:', e);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Count legacy rows (still missing structured fields) — drives the migration banner
  const legacyCount = useMemo(
    () => products.filter(p => !p.brand || !p.parts || p.parts.length === 0 || !p.partSize).length,
    [products],
  );

  // Display helpers — prefer structured fields, fall back to legacy label
  const displayName = (p) => p.displayName || p.label || '—';
  const brandOf = (p) => p.brand || (p.label || '').split(' ')[0] || 'Other';

  const handleDelete = async (productId, productName) => {
    if (!window.confirm(`Delete "${productName}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/presets/${productId}`, { method: 'DELETE' });
      if (r.ok) {
        const data = await r.json().catch(() => ({}));
        if (data.archived) {
          window.alert(`"${productName}" is referenced by production or invoice history and was archived instead of deleted. It is hidden from new documents and can be restored from the Archived section.`);
        }
        refresh();
      }
    } catch (e) { console.error(e); }
  };

  const handleRestore = async (productId) => {
    try {
      const r = await fetch(`${API_URL}/api/presets/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deprecated: false }),
      });
      if (r.ok) refresh();
    } catch (e) { console.error(e); }
  };

  // Run the structured migration. Two-step UX:
  //  1. Dry run → show preview of what will change.
  //  2. Confirm → run for real, then refresh.
  const startMigration = async () => {
    setMigrationOpen(true);
    setMigrationPreview(null);
    setMigrationRunning(true);
    try {
      const r = await fetch(`${API_URL}/api/presets/migrate-structured?dry_run=true`, { method: 'POST' });
      if (r.ok) setMigrationPreview(await r.json());
    } catch (e) { console.error(e); }
    setMigrationRunning(false);
  };

  const confirmMigration = async () => {
    setMigrationRunning(true);
    try {
      const r = await fetch(`${API_URL}/api/presets/migrate-structured?dry_run=false`, { method: 'POST' });
      if (r.ok) {
        setMigrationOpen(false);
        setMigrationPreview(null);
        refresh();
      }
    } catch (e) { console.error(e); }
    setMigrationRunning(false);
  };

  // Merge one preset into another — backend reassigns invoice / batch / stock refs
  const confirmMerge = async () => {
    if (!mergeDialog) return;
    setMerging(true);
    try {
      const r = await fetch(`${API_URL}/api/presets/${mergeDialog.sourceId}/merge-into/${mergeDialog.targetId}`, { method: 'POST' });
      if (r.ok) {
        setMergeDialog(null);
        refresh();
      } else {
        const e = await r.json().catch(() => ({}));
        alert(e.detail || 'Merge failed');
      }
    } catch (e) { console.error(e); }
    setMerging(false);
  };

  const activeProducts = useMemo(() => products.filter(p => !p.deprecated), [products]);
  const archivedProducts = useMemo(() => products.filter(p => p.deprecated), [products]);

  const filteredProducts = activeProducts.filter(p =>
    displayName(p).toLowerCase().includes(searchQuery.toLowerCase())
  );
  const groupedProducts = filteredProducts.reduce((acc, p) => {
    const b = brandOf(p);
    (acc[b] = acc[b] || []).push(p);
    return acc;
  }, {});

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Package className="w-6 h-6 text-amber-500" />
          <h1 className="text-xl sm:text-2xl font-bold text-white">Products</h1>
          <span className="text-sm text-zinc-500">({activeProducts.length})</span>
        </div>
        <Button
          onClick={() => onEditProduct(null)}
          className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
          data-testid="new-product-btn"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">New Product</span>
        </Button>
      </div>

      {/* Migration banner — surfaces only when legacy rows exist */}
      {legacyCount > 0 && (
        <div className="mb-4 p-3 rounded-lg border border-amber-900/50 bg-amber-950/30 flex items-center gap-3" data-testid="migration-banner">
          <AlertCircle className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="flex-1 text-sm text-amber-100">
            <strong>{legacyCount}</strong> product{legacyCount === 1 ? '' : 's'} still use the legacy free-text name format.
            Run migration to parse them into structured fields (brand / parts / size) and detect duplicates.
          </div>
          <Button size="sm" onClick={startMigration} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="migrate-btn">
            Migrate
          </Button>
        </div>
      )}

      {/* Duplicates panel — surfaces only when productKey collisions exist */}
      {duplicates.length > 0 && (
        <div className="mb-4 p-3 rounded-lg border border-rose-900/50 bg-rose-950/20" data-testid="duplicates-panel">
          <div className="flex items-center gap-2 mb-2">
            <GitMerge className="w-5 h-5 text-rose-400" />
            <span className="text-sm font-semibold text-rose-100">
              {duplicates.length} duplicate product{duplicates.length === 1 ? '' : 's'} detected
            </span>
          </div>
          <div className="space-y-2">
            {duplicates.map((d) => (
              <div key={d.productKey} className="p-2 bg-zinc-900/60 rounded border border-zinc-800" data-testid={`duplicate-${d.productKey}`}>
                <div className="text-xs text-zinc-500 mb-1 font-mono">{d.productKey}</div>
                <div className="space-y-1">
                  {d.variants.map((v) => (
                    <div key={v.id} className="flex items-center justify-between text-sm">
                      <span className="text-zinc-300">{v.displayName} <span className="text-xs text-zinc-500">({v.geometry.base}/{v.geometry.leftSide}/{v.geometry.rightSide})</span></span>
                      <div className="flex gap-1">
                        {d.variants.filter(x => x.id !== v.id).map(target => (
                          <Button
                            key={target.id}
                            size="sm"
                            variant="outline"
                            onClick={() => setMergeDialog({ sourceId: v.id, targetId: target.id, sourceName: v.displayName, targetName: target.displayName })}
                            className="h-7 text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                            data-testid={`merge-${v.id}-into-${target.id}`}
                          >
                            ↳ Merge into {`"${target.displayName}"`}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          type="text"
          placeholder="Search products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-zinc-900 border-zinc-700 text-zinc-200"
          data-testid="products-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-12">
          <Package className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500">{searchQuery ? 'No products match your search' : 'No products yet'}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedProducts).sort(([a],[b]) => a.localeCompare(b)).map(([manufacturer, items]) => (
            <div key={manufacturer}>
              <h2 className="text-sm font-medium text-zinc-500 mb-2 px-1">{manufacturer}</h2>
              <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
                <table className="w-full table-fixed">
                  <thead>
                    <tr className="border-b border-zinc-800 text-xs text-zinc-500 uppercase">
                      <th className="text-left py-3 px-4 font-medium w-[28%]">Name</th>
                      <th className="text-center py-3 px-2 font-medium w-[10%]">Part / Size</th>
                      <th className="text-center py-3 px-2 font-medium w-[10%] hidden sm:table-cell">Base</th>
                      <th className="text-center py-3 px-2 font-medium w-[12%] hidden sm:table-cell">Sides</th>
                      <th className="text-center py-3 px-2 font-medium w-[10%] hidden sm:table-cell">Lips</th>
                      <th className="text-center py-3 px-2 font-medium w-[8%] hidden sm:table-cell">Thick</th>
                      <th className="text-center py-3 px-2 font-medium w-[10%]">Sheet</th>
                      <th className="text-right py-3 px-4 font-medium w-[12%]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((product) => {
                      const parts = (product.parts || []).join('/') || (displayName(product).split(' ')[1] || '');
                      const size = product.partSize || '';
                      return (
                        <tr key={product.id} className="border-b border-zinc-800/50 last:border-0 hover:bg-zinc-800/30 transition-colors">
                          <td className="py-3 px-4">
                            <span className="text-zinc-200 font-medium">{displayName(product).replace(`${brandOf(product)} `, '')}</span>
                            {(disp?.variantNoteProducts ?? true) && product.variantNote ? <span className="ml-2 text-xs text-zinc-500">({product.variantNote})</span> : null}
                          </td>
                          <td className="text-center py-3 px-2 text-zinc-300 text-sm">
                            <span className="font-mono">{parts}</span>
                            {size ? <span className="ml-1 text-xs text-zinc-500">{size}</span> : null}
                          </td>
                          <td className="text-center py-3 px-2 text-zinc-400 text-sm hidden sm:table-cell">{product.base}</td>
                          <td className="text-center py-3 px-2 text-zinc-400 text-sm hidden sm:table-cell">{product.leftSide}/{product.rightSide}</td>
                          <td className="text-center py-3 px-2 text-zinc-400 text-sm hidden sm:table-cell">{product.leftLip}/{product.rightLip}</td>
                          <td className="text-center py-3 px-2 text-zinc-400 text-sm hidden sm:table-cell">{product.thickness}</td>
                          <td className="text-center py-3 px-2"><span className="text-amber-500 font-semibold">{calculateSheetWidth(product)}</span></td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => onEditProduct(product)} className="h-8 w-8 p-0 text-zinc-400 hover:text-white hover:bg-zinc-700" data-testid={`edit-product-${product.id}`}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(product.id, displayName(product))} className="h-8 w-8 p-0 text-zinc-400 hover:text-red-400 hover:bg-zinc-700" data-testid={`delete-product-${product.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Archived products — deleted-with-history presets, restorable */}
      {archivedProducts.length > 0 && (
        <div className="mt-8" data-testid="archived-section">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
            data-testid="archived-toggle"
          >
            <Archive className="w-4 h-4" />
            Archived ({archivedProducts.length}) {showArchived ? '▾' : '▸'}
          </button>
          {showArchived && (
            <div className="mt-2 bg-zinc-900/60 rounded-lg border border-zinc-800 divide-y divide-zinc-800/50">
              {archivedProducts.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`archived-product-${p.id}`}>
                  <div>
                    <span className="text-zinc-400 text-sm">{displayName(p)}</span>
                    {(disp?.variantNoteProducts ?? true) && p.variantNote ? <span className="ml-2 text-xs text-zinc-600">({p.variantNote})</span> : null}
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-amber-600/80 border border-amber-900/50 rounded px-1.5 py-0.5">Archived</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRestore(p.id)}
                    className="h-7 text-xs text-zinc-500 hover:text-emerald-400 gap-1.5"
                    data-testid={`restore-product-${p.id}`}
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Restore
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Migration confirm dialog */}
      <AlertDialog open={migrationOpen} onOpenChange={(o) => { if (!o) { setMigrationOpen(false); setMigrationPreview(null); } }}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl" data-testid="migration-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Migrate to structured products</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              I&apos;ll parse every legacy label into structured fields (brand / parts / size). Review the preview before confirming.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {migrationRunning && !migrationPreview ? (
            <div className="flex items-center gap-2 text-zinc-400 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</div>
          ) : migrationPreview ? (
            <div className="space-y-2 max-h-80 overflow-y-auto text-sm">
              <div className="text-xs text-zinc-500 mb-2">
                Will migrate {migrationPreview.migrated} products
                {migrationPreview.needs_review ? <> · <span className="text-amber-400">{migrationPreview.needs_review} need review</span></> : null}
                {migrationPreview.duplicates?.length ? <> · <span className="text-rose-400">{migrationPreview.duplicates.length} duplicates</span></> : null}
              </div>
              <table className="w-full text-xs">
                <thead className="text-zinc-500 uppercase border-b border-zinc-800">
                  <tr><th className="text-left py-1 pr-2">Old label</th><th className="text-left py-1">New name</th></tr>
                </thead>
                <tbody>
                  {migrationPreview.details.map((d) => (
                    <tr key={d.id} className={`border-b border-zinc-800/50 ${d.needsReview ? 'bg-amber-950/20' : ''}`}>
                      <td className="py-1 pr-2 text-zinc-400">{d.originalLabel}</td>
                      <td className="py-1 text-zinc-200">{d.newDisplayName}{d.needsReview ? <span className="ml-2 text-xs text-amber-400">needs review</span> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700" data-testid="migration-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMigration} disabled={migrationRunning || !migrationPreview} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="migration-confirm">
              {migrationRunning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
              Apply migration
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Merge confirm dialog */}
      <AlertDialog open={!!mergeDialog} onOpenChange={(o) => { if (!o) setMergeDialog(null); }}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800" data-testid="merge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Merge products</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Every invoice, batch, and stock row pointing at <strong className="text-zinc-200">{`"${mergeDialog?.sourceName}"`}</strong> will be rewritten to point at <strong className="text-zinc-200">{`"${mergeDialog?.targetName}"`}</strong>, and the source record will be deleted. This is permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700" data-testid="merge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmMerge} disabled={merging} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="merge-confirm">
              {merging ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
              Merge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsPage;
